import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Empty, notification, Space, TreeSelect } from 'antd';

import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { sanitizeDescription } from '@/shared/lib/sanitize';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { Icon } from '@/shared/ui/Icon';
import type { IconName } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './EditableTreeSelect.module.css';
import type { EditableTreeSelectProps, TreeSelectNode } from './types';

const TITLE_ICON_MAP: Partial<Record<string, IconName>> = {
  용어: 'book',
  태그: 'tag',
} as const;

const TAG_STYLES = {
  GAP: 2,
  MARGIN_INLINE_END: 4,
  ICON_SIZE: 12,
} as const;

const isValueObject = <T extends string>(value: unknown): value is { value: T; label: React.ReactNode } => {
  return typeof value === 'object' && value !== null && 'value' in value;
};

export const EditableTreeSelect = <T extends string>({
  value,
  treeData,
  onSave,
  renderDisplay,
  variant = 'card',
  title,
  isLoading = false,
  isUpdating: externalIsUpdating = false,
  placeholder = '항목을 선택하세요',
  emptyText = '항목이 없습니다',
  existingValues,
  className,
  multiple = true,
  disabled = false,
  showIcon = false,
  showTooltip = false,
  popupStyles = {
    maxHeight: 500,
    overflow: 'auto',
    minWidth: 300,
  },
  tooltipPlacement = 'top',
  searchValidation,
}: EditableTreeSelectProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValues, setSelectedValues] = useState<T[]>(value);
  const [searchValue, setSearchValue] = useState<string>('');

  const { validateLength } = useSearchLengthValidation({
    maxLength: searchValidation?.maxLength || 999999,
    warningMessage: searchValidation?.warningMessage || '',
    debounceMs: 1000,
    shouldTruncate: true,
  });

  useEffect(() => {
    if (!isEditing && !externalIsUpdating) {
      setSelectedValues(value);
    }
  }, [value, isEditing, externalIsUpdating]);

  const childToParentMap = useMemo(() => {
    const map = new Map<string, TreeSelectNode>();
    for (const parentNode of treeData) {
      if (parentNode.children) {
        for (const childNode of parentNode.children) {
          map.set(childNode.value, parentNode);
        }
      }
    }
    return map;
  }, [treeData]);

  const valueLabelMap = useMemo(() => {
    const map = new Map<T, string>();

    const traverse = (nodes: TreeSelectNode[]) => {
      nodes.forEach((node) => {
        if (typeof node.title === 'string') {
          map.set(node.value as T, node.title);
        }
        if (node.children) {
          traverse(node.children);
        }
      });
    };

    traverse(treeData);
    return map;
  }, [treeData]);

  const handleEdit = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
  }, [disabled]);

  const handleCancel = useCallback(() => {
    setSelectedValues(value);
    setSearchValue('');
    setIsEditing(false);
  }, [value]);

  const handleChange = useCallback(
    (newValues: T[] | T | { value: T; label: React.ReactNode }[] | { value: T; label: React.ReactNode }) => {
      let valuesToSet: T[] = [];

      if (Array.isArray(newValues)) {
        valuesToSet = newValues.map((item) => (isValueObject<T>(item) ? item.value : item)) as T[];
      } else if (isValueObject<T>(newValues)) {
        valuesToSet = [newValues.value];
      } else {
        valuesToSet = [newValues as T];
      }

      const parentGroups = new Map<string, { parent: TreeSelectNode; children: T[] }>();

      valuesToSet.forEach((val) => {
        const parent = childToParentMap.get(val);
        if (parent && parent.mutuallyExclusive) {
          const existing = parentGroups.get(parent.value);
          if (existing) {
            existing.children.push(val);
          } else {
            parentGroups.set(parent.value, {
              parent,
              children: [val],
            });
          }
        }
      });

      for (const [, group] of parentGroups) {
        if (group.children.length > 1) {
          const parentTitle = typeof group.parent.title === 'string' ? group.parent.title : '해당';

          const conflictingTagLabels = group.children.map((childValue) => valueLabelMap.get(childValue) ?? childValue);

          notification.warning({
            message: `${parentTitle} 분류의 상호배타 태그는 한 번에 하나만 선택할 수 있습니다.`,
            description: `현재 선택한 태그: ${conflictingTagLabels.map((label) => `[${label}]`).join(', ')}는 상호배타 관계의 태그이므로 하나만 선택하고 다시 시도해주세요.`,
            placement: 'bottomLeft',
            duration: 4,
          });
          return;
        }
      }

      setSelectedValues(valuesToSet);
    },
    [childToParentMap, valueLabelMap]
  );

  const hasChanges = useMemo(() => {
    if (selectedValues.length !== value.length) return true;
    const sortedSelected = [...selectedValues].sort();
    const sortedValue = [...value].sort();
    return sortedSelected.some((val, idx) => val !== sortedValue[idx]);
  }, [selectedValues, value]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      setSearchValue('');
      setIsEditing(false);
      return;
    }

    try {
      await onSave(selectedValues);
    } finally {
      setSearchValue('');
      setIsEditing(false);
    }
  }, [selectedValues, hasChanges, onSave]);

  const handleSearch = useCallback(
    (value: string) => {
      if (searchValidation) {
        const validatedValue = validateLength(value);
        setSearchValue(validatedValue);
      } else {
        setSearchValue(value);
      }
    },
    [searchValidation, validateLength]
  );

  const renderTreeNode = useCallback(
    (node: TreeSelectNode) => {
      const shouldShowTooltip = showTooltip && !!node.description;

      if (!shouldShowTooltip) {
        if (showIcon && node.isLeaf) {
          return {
            title: (
              <div className={styles.treeNodeContent}>
                <Icon name="tag" className={styles.tagIcon} />
                <span>{node.title}</span>
              </div>
            ),
          };
        }
        return { title: node.title };
      }

      return {
        title: (
          <Tooltip
            placement={tooltipPlacement}
            mouseEnterDelay={0.5}
            title={
              <div className={styles.tagOptionTooltip}>
                <div className={styles.tagOptionTooltipLabel}>
                  <span className={styles.tagOptionTooltipName}>{node.title}</span>
                  {node.classification && (
                    <span className={styles.tagOptionTooltipClassification}>{node.classification}</span>
                  )}
                </div>
                <div
                  className={styles.tagOptionTooltipDesc}
                  dangerouslySetInnerHTML={{ __html: sanitizeDescription(node.description || '') }}
                />
              </div>
            }>
            <div className={styles.treeNodeContent}>
              {showIcon && <Icon name="tag" className={styles.tagIcon} />}
              <span>{node.title}</span>
            </div>
          </Tooltip>
        ),
      };
    },
    [showTooltip, showIcon, tooltipPlacement]
  );

  const enhancedTreeData = useMemo(() => {
    if (!showTooltip && !showIcon) return treeData;

    const enhanceNode = (node: TreeSelectNode): TreeSelectNode => {
      const enhanced = renderTreeNode(node);
      const originalTitle = typeof node.title === 'string' ? node.title : node.value || '';
      return {
        ...node,
        title: enhanced.title,
        label: originalTitle,
        children: node.children?.map(enhanceNode),
      };
    };

    return treeData.map(enhanceNode);
  }, [treeData, showTooltip, showIcon, renderTreeNode]);

  const filterTreeNode = useCallback((inputValue: string, treeNode: unknown) => {
    const searchTerm = inputValue.toLowerCase();

    if (typeof treeNode === 'object' && treeNode !== null) {
      const node = treeNode as Record<string, unknown>;
      const label = typeof node.label === 'string' ? node.label : null;
      const title = typeof node.title === 'string' ? node.title : null;
      const value = typeof node.value === 'string' ? node.value : '';

      const titleText = label || title || value;
      return titleText.toLowerCase().includes(searchTerm);
    }

    return false;
  }, []);

  const treeSelectValue = useMemo(() => {
    if (!multiple) return selectedValues[0];

    return [...selectedValues]
      .sort((a, b) => {
        if (existingValues) {
          const aExisting = existingValues.has(a);
          const bExisting = existingValues.has(b);

          if (aExisting && !bExisting) return -1;
          if (!aExisting && bExisting) return 1;
        }

        const aLabel = valueLabelMap.get(a) || a;
        const bLabel = valueLabelMap.get(b) || b;
        return aLabel.localeCompare(bLabel, 'ko-KR');
      })
      .map((val) => ({
        value: val,
        label: valueLabelMap.get(val) || val,
      }));
  }, [multiple, selectedValues, valueLabelMap, existingValues]);

  const allExpandedKeys = useMemo(() => {
    const keys: string[] = [];
    const collectKeys = (nodes: TreeSelectNode[]) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          keys.push(node.key);
          collectKeys(node.children);
        }
      });
    };
    collectKeys(enhancedTreeData);
    return keys;
  }, [enhancedTreeData]);

  const renderTag = useCallback(
    (props: { value: T; label: React.ReactNode; closable: boolean; onClose: () => void }) => {
      const { value: tagValue, closable, onClose } = props;
      const label = valueLabelMap.get(tagValue) || tagValue;

      const iconName = title ? TITLE_ICON_MAP[title] : null;

      return (
        <Chip
          icon={iconName && <Icon name={iconName} size={TAG_STYLES.ICON_SIZE} />}
          label={typeof label === 'string' ? label : String(label)}
          onClose={closable ? onClose : undefined}
        />
      );
    },
    [title, valueLabelMap]
  );

  const renderEditMode = () => (
    <div className={variant === 'inline' ? styles.inlineEditContainer : styles.editContainer}>
      <TreeSelect
        multiple={multiple}
        value={treeSelectValue}
        onChange={handleChange}
        treeData={enhancedTreeData}
        placeholder={placeholder}
        loading={isLoading || externalIsUpdating}
        disabled={externalIsUpdating}
        className={variant === 'inline' ? styles.inlineTreeSelect : styles.treeSelect}
        classNames={{
          popup: {
            root: styles.editDropdown,
          },
        }}
        styles={{
          popup: {
            root: popupStyles,
          },
        }}
        treeCheckable={multiple}
        treeLine={true}
        treeCheckStrictly={multiple}
        showSearch={true}
        searchValue={searchValue}
        onSearch={handleSearch}
        filterTreeNode={filterTreeNode}
        allowClear
        treeExpandedKeys={allExpandedKeys}
        tagRender={multiple ? renderTag : undefined}
        notFoundContent={
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={searchValue ? '검색 결과가 없습니다' : emptyText} />
        }
      />
      <Space size="small" className={variant === 'inline' ? styles.inlineButtonGroup : styles.buttonGroup}>
        <Button
          variant="filled"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || externalIsUpdating}
          loading={externalIsUpdating}>
          업데이트
        </Button>
        <Button variant="outlined" size="small" onClick={handleCancel} disabled={externalIsUpdating}>
          취소
        </Button>
      </Space>
    </div>
  );

  const renderDisplayMode = () => {
    if (variant === 'inline') {
      return (
        <div className={`${styles.inlineDisplayContainer} ${className || ''}`}>
          {value.length > 0 ? (
            <>
              <div className={styles.displayContent}>{renderDisplay ? renderDisplay(value) : value.join(', ')}</div>
              {!disabled && (
                <IconButton size="small" onClick={handleEdit} tooltip={title ? `${title} 편집` : '편집'}>
                  <Icon name="edit" size={16} />
                </IconButton>
              )}
            </>
          ) : (
            !disabled && (
              <IconButton size="small" onClick={handleEdit} tooltip={title ? `${title} 편집` : '편집'}>
                <Icon name="edit" size={16} />
              </IconButton>
            )
          )}
        </div>
      );
    }

    if (value.length === 0) {
      return <span className={styles.emptyText}>{emptyText}</span>;
    }

    return (
      <>{renderDisplay ? renderDisplay(value) : <div className={styles.displayContent}>{value.join(', ')}</div>}</>
    );
  };

  const showEditMode = isEditing || externalIsUpdating;

  if (variant === 'inline') {
    return showEditMode ? renderEditMode() : renderDisplayMode();
  }

  return (
    <div className={`${styles.card} ${className || ''}`}>
      <div className={styles.cardHeader}>
        <h5 className={styles.cardTitle}>{title}</h5>
        {!disabled && (
          <IconButton onClick={handleEdit} tooltip={title ? `${title} 편집` : '편집'}>
            <Icon name="edit" size={16} />
          </IconButton>
        )}
      </div>
      {showEditMode ? renderEditMode() : renderDisplayMode()}
    </div>
  );
};
